import { Check, CheckCircle2, Copy, Info } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import type { RunStatus, ReviewStatus, VerifiedVia } from '../lib/types';

/**
 * A small "ⓘ" that toggles an instant popover on click (native `title` tooltips
 * lag ~1s). Rendered in a body portal so it can't be clipped by table/card
 * overflow, and every click stops propagation so it never opens the row behind it.
 */
export function InfoTip({ text, label = 'More information' }: { text: ReactNode; label?: string }) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const open = rect !== null;

  useEffect(() => {
    if (!open) return;
    const close = () => setRect(null);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setRect(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setRect(open ? null : (btnRef.current?.getBoundingClientRect() ?? null));
        }}
        className="inline-flex cursor-pointer align-middle text-amber-400/70 hover:text-amber-300"
      >
        <Info size={12} />
      </button>
      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[60]"
              onClick={(e) => {
                e.stopPropagation();
                setRect(null);
              }}
            />
            <div
              role="tooltip"
              onClick={(e) => e.stopPropagation()}
              style={{
                top: rect.bottom + 6,
                left: Math.max(8, Math.min(rect.left, window.innerWidth - 272)),
              }}
              className="fixed z-[61] w-64 rounded-md border border-ink-700 bg-ink-900 p-2.5 text-[11px] font-normal normal-case leading-relaxed tracking-normal text-slate-300 shadow-xl"
            >
              {text}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

export function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  accent = 'text-accent',
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: LucideIcon;
  accent?: string;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</span>
        {Icon && <Icon size={16} className={accent} />}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums text-slate-100">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export function StatusBadge({ status }: { status: RunStatus }) {
  const map: Record<RunStatus, string> = {
    running: 'border-accent/40 bg-accent/10 text-accent',
    done: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    error: 'border-rose-500/40 bg-rose-500/10 text-rose-300',
    stopped: 'border-slate-500/40 bg-slate-500/10 text-slate-400',
  };
  return (
    <span className={`chip ${map[status]}`}>
      {status === 'running' && (
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
      )}
      {status}
    </span>
  );
}

// How a record's PIN was determined + whether it's been confirmed against HC Tax.
// Lets an operator see, at a glance, which records are human/validated vs machine-guessed.
const VIA_STYLE: Record<VerifiedVia, { label: string; cls: string }> = {
  manual: { label: 'Manual', cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' },
  hcad: { label: 'HCAD', cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' },
  ocr: { label: 'OCR', cls: 'border-sky-500/40 bg-sky-500/10 text-sky-300' },
  auction: { label: 'Auction', cls: 'border-amber-500/40 bg-amber-500/10 text-amber-300' },
  unverified: { label: 'Unverified', cls: 'border-rose-500/40 bg-rose-500/10 text-rose-300' },
};

// What the green check means depends on HOW the PIN was confirmed — don't claim "HC Tax"
// for a parcel HC Tax had no statement for and HCAD confirmed instead.
const CONFIRM_TOOLTIP: Record<VerifiedVia, string> = {
  hcad: 'PIN confirmed via HCAD (HC Tax had no online statement)',
  ocr: 'PIN read from the petition and confirmed on HC Tax',
  manual: 'PIN confirmed manually',
  auction: 'PIN confirmed on HC Tax',
  unverified: 'PIN confirmed',
};

export function VerificationBadge({ via, validated }: { via?: VerifiedVia; validated?: boolean }) {
  const s = VIA_STYLE[via ?? 'unverified'] ?? VIA_STYLE.unverified;
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className={`chip ${s.cls}`}>{s.label}</span>
      {validated ? (
        <span title={CONFIRM_TOOLTIP[via ?? 'unverified'] ?? 'PIN confirmed'} className="inline-flex text-emerald-400">
          <CheckCircle2 size={13} />
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-amber-400/90">
          unconfirmed
          <InfoTip
            label="What does unconfirmed mean?"
            text="PIN provided by the source but not verified on HC Tax — usually because of the property's stage (post-judgment / going to auction or struck off), where HC Tax has no online statement. Not a broken import — check HCAD and the source listing to confirm it by hand."
          />
        </span>
      )}
    </span>
  );
}

export function ReviewBadge({ status }: { status: ReviewStatus }) {
  const map: Record<ReviewStatus, string> = {
    open: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
    resolved: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    archived: 'border-ink-600 bg-ink-800 text-slate-400',
  };
  return <span className={`chip ${map[status]}`}>{status}</span>;
}

export function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-ink-800">
      <div
        className="h-full rounded-full bg-accent transition-[width] duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function Empty({ icon: Icon, title, hint }: { icon: LucideIcon; title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <Icon size={28} className="text-slate-600" />
      <div className="text-sm font-medium text-slate-300">{title}</div>
      {hint && <div className="max-w-sm text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export function CardIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent-soft/15 text-accent">
      <Icon size={18} />
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-ink-600 bg-ink-950 text-accent focus:ring-accent"
      />
      {label}
    </label>
  );
}

export function Pager({
  page,
  pageSize,
  count,
  total,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  noun = 'records',
}: {
  page: number;
  pageSize: number;
  count: number;
  total: number | null;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  noun?: string;
}) {
  const start = count === 0 ? 0 : page * pageSize + 1;
  const end = page * pageSize + count;
  return (
    <div className="flex items-center justify-between border-t border-ink-800 px-4 py-2 text-xs text-slate-400">
      <span className="tabular-nums">
        {total !== null ? (
          <>
            {start.toLocaleString()}–{end.toLocaleString()} of{' '}
            <span className="font-medium text-slate-300">{total.toLocaleString()}</span> {noun}
          </>
        ) : (
          `${start}–${end} ${noun}`
        )}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="rounded-md border border-ink-700 px-2 py-1 hover:bg-ink-800 disabled:opacity-40"
          disabled={!hasPrev}
          onClick={onPrev}
        >
          Prev
        </button>
        <button
          type="button"
          className="rounded-md border border-ink-700 px-2 py-1 hover:bg-ink-800 disabled:opacity-40"
          disabled={!hasNext}
          onClick={onNext}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-ink-600 border-t-accent" />
    </div>
  );
}

/** Copies `value` (the thing named by `what`, default "PIN") to the clipboard, then
 *  opens `url` in a new tab — the reliable affordance for external sites with no
 *  stable deep link: HCAD (bot challenge), HC Tax (fallback), and District Clerk
 *  (per-case session-token gate; copies the case number). The user pastes the copied
 *  value into the site's own search. */
export function CopyOpenButton({
  value,
  url,
  label,
  what = 'PIN',
  openNoun = 'search',
}: {
  value: string;
  url: string;
  label: string;
  /** What we copy to the clipboard — defaults to "PIN"; e.g. "case number" for District Clerk. */
  what?: string;
  /** What the opened page is — defaults to "search"; e.g. "statement" for an HC Tax deep link. */
  openNoun?: string;
}) {
  const [copied, setCopied] = useState(false);
  async function go() {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be blocked; still open the page
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  return (
    <button
      type="button"
      className="btn-ghost"
      onClick={go}
      title={`Copy ${what} ${value} and open ${label} ${openNoun}`}
    >
      {copied ? <Check size={15} className="text-emerald-400" /> : <Copy size={15} />}
      {label}
    </button>
  );
}
